import Axios from './Base'

const PREFIX = '/_api_'
const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24

export default class Gifts {
  constructor(token) {
    this.token = `Bearer ${token.token}`
    this.wedId = token.token_payload.wed_id
    this.axios = Axios({
      cache: false,
      authorization: this.token,
      wedId: this.wedId,
    })
  }

  getGiftsSummary() {
    return this.axios
      .get(`${PREFIX}/getGiftsSummary?wed=${this.wedId}`)
      .then((response) => response.data)
  }

  getGiftsCategories() {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .get(`${PREFIX}/getGiftsCategories?wed=${this.wedId}`, {
        id: 'gifts-categories',
        cache: {
          ttl: ONE_DAY_IN_MS,
        },
      })
      .then((response) => response.data)
  }

  getGiftsList() {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .get(`${PREFIX}/getGiftsList?wed=${this.wedId}`, {
        id: 'gifts-list',
      })
      .then((response) => response.data)
  }

  getGiftsList() {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .get(`${PREFIX}/getGiftsList?wed=${this.wedId}`, {
        id: 'gifts-list',
      })
      .then((response) => response.data)
  }

  getGiftById(giftId) {
    const axiosWithCache = Axios({
      cache: false,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .get(`${PREFIX}/getGiftsList?wed=${this.wedId}&id=${giftId}`)
      .then((response) => response.data)
  }

  delGiftsFromList() {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(
        `${PREFIX}/delGiftsFromList?wed=${this.wedId}`,
        {},
        {
          cache: {
            update: {
              'gifts-list': 'delete',
            },
          },
        }
      )
      .then((response) => response.data)
      .catch(() => false)
  }

  delGift(giftId) {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(
        `${PREFIX}/delGift?wed=${this.wedId}`,
        { id: giftId },
        {
          cache: {
            update: {
              'gifts-list': (giftsListCached, response) => {
                if (giftsListCached.state !== 'cached') return 'ignore'
                giftsListCached.data.data.data.list =
                  giftsListCached.data.data.data.list.filter(
                    (gift) => gift.id !== response.data.data.id
                  )
                return giftsListCached
              },
            },
          },
        }
      )
      .then((response) => response.data)
      .catch(() => false)
  }

  async getGiftsFromCatalog() {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })

    const responseGifts = await axiosWithCache.get(
      `${PREFIX}/getGiftsFromCatalog?wed=${this.wedId}`,
      {
        id: 'gifts-from-catalog',
        cache: {
          ttl: ONE_DAY_IN_MS,
        },
      }
    )
    const responseCategories = await this.getGiftsCategories()

    const getCategoryName = (gift) => {
      const category = responseCategories.data.find(
        (category) => category.id === gift.id_categoria
      )
      return category.nome || ''
    }

    const gifts = responseGifts.data.data.map((gift) => ({
      ...gift,
      categoria: getCategoryName(gift),
    }))

    return {
      data: gifts,
    }
  }

  getLastReceivedGifts(query) {
    return this.axios
      .get(`${PREFIX}/getLastReceivedGifts?wed=${this.wedId}&q=${query}`)
      .then((response) => response.data)
  }

  addGiftCustom(data) {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(`${PREFIX}/addGiftCustom?wed=${this.wedId}`, data, {
        cache: {
          update: {
            'gifts-list': 'delete',
          },
        },
      })
      .then((response) => {
        if (!response.data.error) return response.data
        throw (
          'Error on creating gift ' +
            JSON.stringify(response.data.error?.errors) || ''
        )
      })
  }

  editGift(data) {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(`${PREFIX}/editGift?wed=${this.wedId}`, data, {
        cache: {
          update: {
            'gifts-list': 'delete',
          },
        },
      })
      .then((response) => {
        if (!response.data.error) return response.data
        throw (
          'Error on editing gift ' +
            JSON.stringify(response.data.error?.errors) || ''
        )
      })
  }

  getGiftsAutomaticListsInfo() {
    return this.axios
      .get(`${PREFIX}/getGiftsAutomaticListsInfo?wed=${this.wedId}`)
      .then((response) => response.data)
  }

  getGiftsAutomaticList(id) {
    return this.axios
      .get(`${PREFIX}/getGiftsAutomaticList?wed=${this.wedId}&id=${id}`)
      .then((response) => response.data)
  }

  addGiftsAutomaticList(data) {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(`${PREFIX}/addGiftsAutomaticList?wed=${this.wedId}`, data, {
        cache: {
          update: {
            'gifts-list': 'delete',
          },
        },
      })
      .then((response) => response.data)
      .catch(() => false)
  }

  thankGift(data) {
    return this.axios
      .post(`${PREFIX}/editGrateful?wed=${this.wedId}`, data)
      .then((response) => response.data)
      .catch(() => false)
  }

  uploadGiftImage(file) {
    return this.axios
      .post(`${PREFIX}/uploadGiftImage?wed=${this.wedId}`, file, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => response.data)
      .catch(() => false)
  }

  getTaxes() {
    return this.axios
      .get(`${PREFIX}/getTaxes?wed=${this.wedId}`)
      .then((response) => response.data)
  }

  editTaxes(data) {
    return this.axios
      .post(`${PREFIX}/editTaxes?wed=${this.wedId}`, data)
      .then((response) => response.data)
  }

  getGiftListConfig() {
    return this.axios
      .get(`${PREFIX}/getGiftListConfig?wed=${this.wedId}`)
      .then((response) => response.data)
  }

  editGiftListOrder(value) {
    var data = {
      order: value,
    }
    return this.axios
      .post(`${PREFIX}/editGiftListOrder?wed=${this.wedId}`, data)
      .then((response) => response.data)
      .catch(() => false)
  }

  addGiftFromCatalog(giftId) {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(
        `${PREFIX}/addGiftFromCatalog?wed=${this.wedId}&id=${giftId}`,
        {},
        {
          cache: {
            update: {
              'gifts-list': 'delete',
            },
          },
        }
      )
      .then((response) => response.data)
      .catch(() => false)
  }

  editGiftListVisibility(value) {
    var data = {
      published: value,
    }
    return this.axios
      .post(`${PREFIX}/editGiftListVisibility?wed=${this.wedId}`, data)
      .then((response) => response.data)
      .catch(() => false)
  }

  getGiftsExternalList() {
    return this.axios
      .get(`${PREFIX}/getGiftsExternalList?wed=${this.wedId}`)
      .then((response) => response.data)
      .catch(() => false)
  }

  editGiftsExternalList(data) {
    return this.axios
      .post(`${PREFIX}/editGiftsExternalList?wed=${this.wedId}`, data)
      .then((response) => response.data)
      .catch(() => false)
  }

  getGiftsExternalListStores() {
    return this.axios
      .get(`${PREFIX}/getGiftsExternalListStores?wed=${this.wedId}`)
      .then((response) => response.data)
      .catch(() => false)
  }

  saveGiftsListsSurvey(data) {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(`${PREFIX}/saveGiftsListsSurvey?wed=${this.wedId}`, data, {
        cache: {
          update: {
            'gifts-user-surveys': (surveysCached, response) => {
              if (surveysCached.state !== 'cached') return 'ignore'
              surveysCached.data.data.data = response.data.data
              return surveysCached
            },
          },
        },
      })
      .then((response) => response.data)
      .catch(() => false)
  }

  getGiftsUserSurveys() {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })

    return axiosWithCache
      .get(`${PREFIX}/getGiftsUserSurveys?wed=${this.wedId}`, {
        id: 'gifts-user-surveys',
      })
      .then((response) => response.data)
      .catch(() => false)
  }

  async getGiftsUserSurveysByType(type) {
    return this.getGiftsUserSurveys().then((response) => {
      const surveys = response.data
      if (Array.isArray(surveys) && surveys.length) {
        return surveys.filter((survey) => survey.tipo === type)
      }
      return []
    })
  }

  async getGiftsConfigs() {
    return this.axios
      .get(`${PREFIX}/getGiftsInfo?wed=${this.wedId}`)
      .then((response) => response.data)
      .catch(() => false)
  }

  async savePostalcardsConfig(option) {
    const axiosWithCache = Axios({
      authorization: this.token,
      wedId: this.wedId,
    })

    const payload = { embutir_card: option ? 1 : 0 }

    return axiosWithCache
      .post(`${PREFIX}/editGiftsEmbedCards?wed=${this.wedId}`, payload)
      .then((response) => response.data)
      .catch(() => false)
  }

  editGiftsBulkPercentage(percentage) {
    const axiosWithCache = Axios({
      cache: true,
      authorization: this.token,
      wedId: this.wedId,
    })
    return axiosWithCache
      .post(
        `${PREFIX}/editGiftsBulkPercentage?wed=${this.wedId}`,
        { percentage },
        {
          cache: {
            update: {
              'gifts-list': 'delete',
            },
          },
        }
      )
      .then((response) => response.data)
  }
}
